import { getDeviceInfo } from '../Upshow';
import SocketService from './SocketService';

export const LOGS_EVENT = 'device-logs';

export const TRANSPORT_HTTP = 'http';
export const TRANSPORT_SOCKET = 'socket';
export const TRANSPORT_SOCKET_DEGRADED = 'socket-degraded';

// Also the heartbeat: with nothing to send the tick still emits an empty frame, whose ack
// is what tells the device it is connected. Matches the cadence of the POST it replaces.
export const FLUSH_INTERVAL_MS = Number(process.env.REACT_APP_DEVICE_TELEMETRY_INTERVAL_MS) || 3_000;
export const MAX_BATCH_LINES = 20;
// Mandatory, not tuning: socket.io only arms the ack timer when a timeout is set, and
// without it the callback for every discarded frame is retained forever.
export const ACK_TIMEOUT_MS = 5_000;
// A frame over socket.io maxHttpBufferSize (1MB default) disconnects the client, which
// demotes the transport and can loop, so stay well under it.
export const MAX_FRAME_BYTES = 256 * 1024;
// Reserved out of the frame for the drop report, which is prepended after the batch is sized.
export const DROP_REPORT_BYTES = 128;
// A disconnect is usually a blip: hold the lines instead of racing them to HTTP, and
// only give up on the socket once this window expires. It has to outlast the manager's
// first retry (reconnectionDelay 30s randomised to [15s, 45s]) plus CONNECT_SETTLE_MS,
// or a socket-server deploy demotes half the fleet on a reconnect that would have worked.
export const DEGRADED_GRACE_MS = 60_000;
// Ceiling for a degraded device or a log storm outrunning the frame cap.
export const MAX_BUFFERED_LINES = 1_000;
// A connection has to hold this long before it owns logs: volatile.emit gives no delivery
// signal, so a flush into a socket that is about to drop loses the lines silently.
export const CONNECT_SETTLE_MS = 5_000;
export const FLAP_THRESHOLD = 3;
export const FLAP_WINDOW_MS = 120_000;
export const BREAKER_COOLDOWN_MS = 600_000;

// String.length counts UTF-16 units; a CJK or emoji line is up to 3x that on the wire, so
// counting characters is how a frame under the 256KB cap still trips the server's 1MB limit.
export function utf8Length (str) {
    let bytes = 0;

    for (let i = 0; i < str.length; i++) {
        const code = str.charCodeAt(i);

        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff && i + 1 < str.length) { bytes += 4; i++; }
        else bytes += 3;
    }

    return bytes;
}

// The lines are not the whole frame: ts, deviceInfo and all three arrays ride along.
export function frameBudget (deviceInfo) {
    const envelope = { ts: Date.now(), deviceInfo, logs: [], metrics: [], analytics: [] };
    return MAX_FRAME_BYTES - utf8Length(JSON.stringify(envelope)) - DROP_REPORT_BYTES;
}

// Set by the loader URL (index.js). Absent on an older loader, where logs stay on the socket.
export function logsSocketEnabled () {
    return window.logs_socket_enabled !== false;
}

// These devices never reached app_events either. The heartbeat makes the server synthesize
// a keepalive and push devices_update, so emitting would report presence they report none of.
export function telemetrySuppressed () {
    return Boolean(window.st || window.inactive || window.demo);
}

export function buildDeviceInfo (info = {}) {
    return {
        version: info.version,
        uiVersion: info.uiVersion,
        model: info.model,
        serial: info.serial,
        sessionId: info.sessionId,
        deviceUptime: info.DeviceUptime,
        osVersion: info.osVersion
    };
}

export class DeviceLogsSocket {
    transport = TRANSPORT_HTTP;
    buffer = [];
    // Own buffers: they get the frame budget before log lines do.
    metricsBuffer = [];
    analyticsBuffer = [];
    droppedLines = 0;
    droppedSideItems = 0;

    socket = null;
    spillHandler = null;
    connectivityHandler = null;
    deviceInfoProvider = getDeviceInfo;

    flushTimer = null;
    graceTimer = null;
    settleTimer = null;
    breakerTimer = null;
    breakerOpen = false;
    flapCount = 0;
    flapTimers = [];
    listeners = null;

    register ({ socket = SocketService.socket, spillToHttp = null, onConnectivity = null, deviceInfoProvider = getDeviceInfo } = {}) {
        this.unregister();

        this.spillHandler = spillToHttp;
        this.connectivityHandler = onConnectivity;
        this.deviceInfoProvider = deviceInfoProvider;

        // Kill switch: logs go back to the HTTP path and nothing here observes the socket, which
        // keeps connecting for commands, heartbeat metrics and live content.
        if (!logsSocketEnabled()) return;

        this.socket = socket ?? null;

        if (!this.socket) return;

        this.listeners = {
            connect: () => this._onConnect(),
            disconnect: () => this._onDisconnect(),
            reconnect_failed: () => this._onReconnectFailed(),
            // A reload takes the buffer with it, and those lines are the ones that explain
            // the reload.
            pagehide: () => this.flush()
        };

        this.socket.on('connect', this.listeners.connect);
        this.socket.on('disconnect', this.listeners.disconnect);
        this.socket.on('reconnect_failed', this.listeners.reconnect_failed);
        // socket.io v4 emits reconnect_failed on the manager, not on the socket
        this.socket.io?.on?.('reconnect_failed', this.listeners.reconnect_failed);
        window.addEventListener('pagehide', this.listeners.pagehide);

        if (this.socket.connected) this._onConnect();
    }

    unregister () {
        if (this.socket && this.listeners) {
            this.socket.off?.('connect', this.listeners.connect);
            this.socket.off?.('disconnect', this.listeners.disconnect);
            this.socket.off?.('reconnect_failed', this.listeners.reconnect_failed);
            this.socket.io?.off?.('reconnect_failed', this.listeners.reconnect_failed);
            window.removeEventListener('pagehide', this.listeners.pagehide);
        }

        this._clearFlushTimer();
        this._clearGraceTimer();
        this._clearSettleTimer();
        this._clearBreaker();
        this.listeners = null;
        this.socket = null;
    }

    getTransport () {
        return this.transport;
    }

    getBufferedCount () {
        return this.buffer.length;
    }

    ownsLogs () {
        return this.transport !== TRANSPORT_HTTP;
    }

    push (line) {
        if (!this.ownsLogs()) return false;

        this.buffer.push(line);
        this._trimBuffer();

        if (this.buffer.length >= MAX_BATCH_LINES) {
            this.flush();
        } else if (this.transport === TRANSPORT_SOCKET) {
            this._startFlushTimer();
        }

        return true;
    }

    // No early flush: they are low volume and the tick is already as fast as the POST.
    pushMetric (metric) {
        if (!this.ownsLogs()) return false;

        this.metricsBuffer.push(metric);
        this._trimSideBuffer(this.metricsBuffer);
        if (this.transport === TRANSPORT_SOCKET) this._startFlushTimer();

        return true;
    }

    pushAnalytic (analytic) {
        if (!this.ownsLogs()) return false;

        this.analyticsBuffer.push(analytic);
        this._trimSideBuffer(this.analyticsBuffer);
        if (this.transport === TRANSPORT_SOCKET) this._startFlushTimer();

        return true;
    }

    hasPending () {
        return this.buffer.length > 0 || this.metricsBuffer.length > 0 || this.analyticsBuffer.length > 0;
    }

    flush () {
        this._clearFlushTimer();

        if (this.transport !== TRANSPORT_SOCKET || !this.socket?.connected) return;

        // Rearmed, not stopped: `inactive` flips at runtime and must resume without a reconnect.
        if (telemetrySuppressed()) {
            this._startFlushTimer();
            return;
        }

        // socket.io discards a volatile packet while the transport is mid-write, a state
        // `connected` does not express, and _takeBatch() empties the buffer before the emit.
        // Only an engine that says so blocks a flush; an unknown shape is assumed writable.
        if (this.socket.io?.engine?.transport?.writable === false) {
            if (this.hasPending()) this._startFlushTimer();
            return;
        }

        const deviceInfo = buildDeviceInfo(this.deviceInfoProvider?.());
        const frame = frameBudget(deviceInfo);
        let remaining = frame;

        // These claim the frame first: a log storm is exactly when the error metrics it
        // produces matter most, and the logs would otherwise eat the whole frame.
        const metrics = this._takeFrom(this.metricsBuffer, remaining, frame);
        remaining -= utf8Length(JSON.stringify(metrics));
        const analytics = this._takeFrom(this.analyticsBuffer, remaining, frame);
        remaining -= utf8Length(JSON.stringify(analytics));

        const logs = this._takeBatch(remaining, frame);

        // Counted, not taken: a report zeroed into a frame that never leaves loses the loss.
        const dropped = this.droppedLines;
        const droppedSide = this.droppedSideItems;
        if (dropped > 0 || droppedSide > 0) logs.unshift(this._dropReport(dropped));

        // Empty frames are sent too: the ack is the connectivity probe.
        try {
            // volatile: a plain emit would replay from socket.io's offline queue the lines
            // the grace window already spilled. The ack is registered before that discard
            // check, so a dropped frame still reports back.
            this.socket
                .timeout(ACK_TIMEOUT_MS)
                .volatile
                // ownsTelemetry: this client suppresses its POST, so the server owes it the
                // keepalive and the devices_update beacon instead of app_events.
                .emit(LOGS_EVENT, { ts: Date.now(), ownsTelemetry: true, deviceInfo, logs, metrics, analytics }, (err) => this._onAck(err));
        } catch (e) {
            if (dropped > 0 || droppedSide > 0) logs.shift();
            this.buffer = logs.concat(this.buffer);
            this.metricsBuffer = metrics.concat(this.metricsBuffer);
            this.analyticsBuffer = analytics.concat(this.analyticsBuffer);
            this._trimBuffer();
            this._trimSideBuffer(this.metricsBuffer);
            this._trimSideBuffer(this.analyticsBuffer);
            this._startFlushTimer();
            this._reportConnectivity(false);
            return;
        }

        // Cleared by the amount actually reported, so a drop counted mid-failure survives.
        this.droppedLines -= dropped;
        this.droppedSideItems -= droppedSide;

        // Always rearmed, pending or not: the tick is the heartbeat.
        this._startFlushTimer();
    }

    // An ack proves a live round trip; a timeout means discarded or unanswered, which is
    // what a failed POST used to mean.
    _onAck (err) {
        this._reportConnectivity(!err);
    }

    _reportConnectivity (ok) {
        if (this.connectivityHandler) this.connectivityHandler(ok);
    }

    _trimBuffer () {
        const overflow = this.buffer.length - MAX_BUFFERED_LINES;
        if (overflow > 0) {
            this.buffer.splice(0, overflow);
            this.droppedLines += overflow;
        }
    }

    // Counted separately from log lines: losing impressions is not the same event.
    _trimSideBuffer (buffer) {
        const overflow = buffer.length - MAX_BUFFERED_LINES;
        if (overflow > 0) {
            buffer.splice(0, overflow);
            this.droppedSideItems += overflow;
        }
    }

    // Discarded only when it cannot fit an EMPTY frame; anything else waits for the next
    // tick, or a frame metrics already filled would drop perfectly sendable items.
    // `project` shapes what goes on the wire, `onDrop` names which counter the loss belongs
    // to — losing impressions is not the same event as losing debug lines.
    _fill (buffer, remaining, hardLimit, project, onDrop) {
        const taken = [];
        let bytes = 2;

        while (buffer.length > 0) {
            const item = project(buffer[0]);
            let size;

            try {
                size = utf8Length(JSON.stringify(item)) + 1;
            } catch (e) {
                size = Infinity;
            }

            if (size > hardLimit) { buffer.shift(); onDrop(); continue; }
            if (bytes + size > remaining) break;

            bytes += size;
            buffer.shift();
            taken.push(item);
        }

        return taken;
    }

    _takeFrom (buffer, remaining, hardLimit) {
        return this._fill(buffer, remaining, hardLimit, item => item, () => this.droppedSideItems++);
    }

    _dropReport (count) {
        const stuff = { count };
        if (this.droppedSideItems > 0) stuff.metricsAndAnalytics = this.droppedSideItems;

        return {
            name: 'event.device_logs_dropped',
            ts: Date.now(),
            stuff
        };
    }

    _takeDropReport () {
        if (this.droppedLines === 0) return null;

        const report = this._dropReport(this.droppedLines);
        this.droppedLines = 0;

        return report;
    }

    // Same two-limit rule, projected to the {name, ts, stuff} shape the server reshapes.
    _takeBatch (remaining, hardLimit = remaining) {
        return this._fill(
            this.buffer, remaining, hardLimit,
            ({ name, ts, stuff }) => ({ name, ts, stuff }),
            () => this.droppedLines++
        );
    }

    _onConnect () {
        if (this.breakerOpen) return;
        this._startSettleTimer();
    }

    _onDisconnect () {
        this._clearSettleTimer();

        if (this.breakerOpen) return;

        this._recordFlap();

        // The grace deadline is armed on the socket -> degraded edge only. A reconnect that
        // never survives the settle period leaves the transport degraded, so it cannot push
        // the deadline out and the fallback still trips on schedule.
        if (this.transport === TRANSPORT_SOCKET) {
            this.transport = TRANSPORT_SOCKET_DEGRADED;
            this._clearFlushTimer();
            this.graceTimer = setTimeout(() => this._fallBackToHttp(), DEGRADED_GRACE_MS);
        }

        if (this.flapCount >= FLAP_THRESHOLD) this._tripBreaker();
    }

    _onReconnectFailed () {
        this._fallBackToHttp();
    }

    // Unconditional: the tick is the heartbeat, so an idle device keeps proving it is up.
    _promote () {
        this._clearGraceTimer();
        this.transport = TRANSPORT_SOCKET;
        this._startFlushTimer();
    }

    _tripBreaker () {
        this._clearFlaps();
        this._clearSettleTimer();

        this.breakerOpen = true;
        this.breakerTimer = setTimeout(() => {
            this.breakerTimer = null;
            this.breakerOpen = false;
            // No further 'connect' will fire on a socket that stabilised during the cooldown.
            if (this.socket?.connected) this._startSettleTimer();
        }, BREAKER_COOLDOWN_MS);

        this._fallBackToHttp();
    }

    _recordFlap () {
        this.flapCount++;

        const timer = setTimeout(() => {
            this.flapTimers = this.flapTimers.filter(t => t !== timer);
            this.flapCount--;
        }, FLAP_WINDOW_MS);

        this.flapTimers.push(timer);
    }

    _clearFlaps () {
        this.flapTimers.forEach(clearTimeout);
        this.flapTimers = [];
        this.flapCount = 0;
    }

    _clearBreaker () {
        this._clearFlaps();
        if (this.breakerTimer) clearTimeout(this.breakerTimer);
        this.breakerTimer = null;
        this.breakerOpen = false;
    }

    // Whatever the socket held becomes the next HTTP body, so the fallback is lossless.
    _fallBackToHttp () {
        this._clearGraceTimer();
        this._clearFlushTimer();
        this.transport = TRANSPORT_HTTP;

        const logs = this.buffer;
        const metrics = this.metricsBuffer;
        const analytics = this.analyticsBuffer;
        this.buffer = [];
        this.metricsBuffer = [];
        this.analyticsBuffer = [];

        const dropped = this._takeDropReport();
        if (dropped) logs.push(dropped);

        if (this.spillHandler && (logs.length > 0 || metrics.length > 0 || analytics.length > 0)) {
            this.spillHandler({ logs, metrics, analytics });
        }
    }

    _startSettleTimer () {
        if (this.settleTimer) return;
        this.settleTimer = setTimeout(() => {
            this.settleTimer = null;
            if (!this.breakerOpen) this._promote();
        }, CONNECT_SETTLE_MS);
    }

    _clearSettleTimer () {
        if (!this.settleTimer) return;
        clearTimeout(this.settleTimer);
        this.settleTimer = null;
    }

    _startFlushTimer () {
        if (this.flushTimer) return;
        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this.flush();
        }, FLUSH_INTERVAL_MS);
    }

    _clearFlushTimer () {
        if (!this.flushTimer) return;
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
    }

    _clearGraceTimer () {
        if (!this.graceTimer) return;
        clearTimeout(this.graceTimer);
        this.graceTimer = null;
    }
}

export default new DeviceLogsSocket();
